import React, { useMemo, useState } from 'react';
import { AlertCircle, IndianRupee, Plus, Scale, Send, Trash2 } from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useColors } from '../../hooks/useColors';
import type { OrderDetail } from './OrderDetailPanel';

interface FinalBillCardProps {
  order: OrderDetail;
  onRefresh: () => void;
}

interface AdditionalItem {
  label: string;
  qty: number | '';
  unit_price: number | '';
}

const money = (value: number) => Number((Number.isFinite(value) ? value : 0).toFixed(2));
const MIN_PAYABLE_FLOOR = 149; // mirrors backend order.controller.js / final-bill.controller.js
// No GST line - taxes are inclusive in the item/kg prices. Final = subtotal −
// discount + delivery, exactly like order creation.

const parseNumberInput = (value: string): number | '' => {
  if (value.trim() === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

const isWeightUnit = (unit?: string | null) => String(unit || '').toLowerCase() === 'per_kg';

// Trim trailing zeros so 3, 3.5, 3.75 read naturally.
const fmtWeight = (kg: number) => String(money(kg));

// Preview mirror of backend computeCouponDiscount (the server re-applies it
// authoritatively on submit; this is only for the live breakdown).
const previewDiscount = (coupon: OrderDetail['coupon'], subtotal: number): number => {
  if (!coupon) return 0;
  let d =
    coupon.discount_type === 'percent'
      ? (subtotal * Number(coupon.discount_value)) / 100
      : Number(coupon.discount_value);
  if (coupon.discount_type === 'percent' && coupon.max_discount != null) {
    d = Math.min(d, Number(coupon.max_discount));
  }
  d = Math.min(d, Math.max(0, subtotal));
  return money(d);
};

export default function FinalBillCard({ order, onRefresh }: FinalBillCardProps) {
  const colors = useColors();
  const { addToast } = useToast();

  // Split the order's lines into weight-priced (per_kg) and counted. The per_kg
  // lines are what the hub re-weighs; counted lines carry through unchanged.
  const { weightLines, countLines } = useMemo(() => {
    const w: { id: string; label: string; origQty: number; unitPrice: number; origTotal: number }[] = [];
    const c: { label: string; qty: number; unit_price: number; total: number }[] = [];
    for (const item of order.items || []) {
      const origQty = Number(item.quantity || 0);
      const origTotal = Number(item.total_price || 0);
      const unitPrice = origQty > 0 ? money(origTotal / origQty) : origTotal;
      const label = item.name || item.service_name || 'Laundry item';
      if (isWeightUnit(item.unit)) {
        w.push({ id: item.id, label, origQty, unitPrice, origTotal: money(origTotal) });
      } else {
        c.push({ label, qty: origQty, unit_price: unitPrice, total: money(origTotal) });
      }
    }
    return { weightLines: w, countLines: c };
  }, [order.items]);

  const hasWeightLines = weightLines.length > 0;

  // Actual (hub) weight per per_kg line. Seed from the already-verified weight
  // when there's a single line, otherwise from each line's estimated weight.
  const [actualWeights, setActualWeights] = useState<Record<string, number | ''>>(() => {
    const seed: Record<string, number | ''> = {};
    weightLines.forEach((line, idx) => {
      if (weightLines.length === 1 && order.weight_kg != null) seed[line.id] = Number(order.weight_kg);
      else seed[line.id] = idx === 0 && order.weight_kg != null ? Number(order.weight_kg) : line.origQty;
    });
    return seed;
  });
  const [garmentCount, setGarmentCount] = useState<number | ''>(order.garment_count ?? '');
  const [notes, setNotes] = useState('');
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoMimeType, setPhotoMimeType] = useState<string | null>(null);
  const [additionalItems, setAdditionalItems] = useState<AdditionalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  // Recompute the per_kg lines from the entered actual weight.
  const recomputedWeightLines = useMemo(
    () =>
      weightLines.map((line) => {
        const actual = Number(actualWeights[line.id] || 0);
        return {
          label: line.label,
          qty: actual,
          unit_price: line.unitPrice,
          total: money(line.unitPrice * actual),
        };
      }),
    [weightLines, actualWeights]
  );

  const additionalLines = useMemo(
    () =>
      additionalItems
        .filter((item) => item.label.trim() && Number(item.qty) > 0)
        .map((item) => {
          const qty = Number(item.qty || 0);
          const unitPrice = Number(item.unit_price || 0);
          return { label: item.label.trim(), qty, unit_price: unitPrice, total: money(qty * unitPrice) };
        }),
    [additionalItems]
  );

  const allLines = useMemo(
    () => [...recomputedWeightLines, ...countLines, ...additionalLines],
    [recomputedWeightLines, countLines, additionalLines]
  );

  const estimatedWeight = useMemo(() => money(weightLines.reduce((s, l) => s + l.origQty, 0)), [weightLines]);
  const actualWeight = useMemo(
    () => money(weightLines.reduce((s, l) => s + Number(actualWeights[l.id] || 0), 0)),
    [weightLines, actualWeights]
  );

  // Live breakdown - mirrors the backend recompute (coupon → delivery floor → GST).
  const subtotal = useMemo(() => money(allLines.reduce((s, l) => s + l.total, 0)), [allLines]);
  const discount = useMemo(() => previewDiscount(order.coupon, subtotal), [order.coupon, subtotal]);
  const deliveryFee = useMemo(() => (subtotal >= MIN_PAYABLE_FLOOR ? 0 : money(MIN_PAYABLE_FLOOR - subtotal)), [subtotal]);
  const grandTotal = useMemo(() => money(subtotal + deliveryFee - discount), [subtotal, deliveryFee, discount]);

  const estimatedTotal = money(Number(order.estimated_amount ?? order.total ?? 0));
  const totalDelta = money(grandTotal - estimatedTotal);

  const isPending = order.final_bill_status === 'pending_customer';
  const showCard = order.status === 'at_facility' || isPending;

  if (!showCard) return null;

  const setAdditionalItem = (index: number, patch: Partial<AdditionalItem>) => {
    setAdditionalItems((prev) => prev.map((row, idx) => (idx === index ? { ...row, ...patch } : row)));
  };
  const addAdditionalItem = () => setAdditionalItems((prev) => [...prev, { label: '', qty: 1, unit_price: '' }]);
  const removeAdditionalItem = (index: number) => setAdditionalItems((prev) => prev.filter((_, idx) => idx !== index));

  const onPickPhoto: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const b64 = result.includes(',') ? result.split(',')[1] : result;
      setPhotoBase64(b64 || null);
      setPhotoMimeType(file.type || 'image/jpeg');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (allLines.length === 0) {
      setError('Add at least one bill line item before posting.');
      return;
    }
    if (hasWeightLines && actualWeight <= 0) {
      setError('Enter the actual hub weight before recalculating.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await adminApi.postFinalBill(order.id, {
        // The verified total weight = sum of the per_kg line weights.
        weight_kg: hasWeightLines ? actualWeight : undefined,
        garment_count: typeof garmentCount === 'number' ? Math.round(garmentCount) : undefined,
        lines: allLines,
        delta_reason: notes.trim() || undefined,
        reweigh_photo_b64: photoBase64 || undefined,
        reweigh_photo_mime_type: photoMimeType || undefined,
        kind: photoBase64 ? 'reweigh_photo' : undefined,
      });
      addToast('Bill recalculated. Customer notified for approval', 'success');
      onRefresh();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.code || 'Failed to post final bill');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await adminApi.resendFinalBill(order.id);
      addToast('Reminder sent to customer', 'success');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to resend final bill reminder');
    } finally {
      setResending(false);
    }
  };

  const inputStyle = { width: '100%', marginTop: 4, padding: 8, border: `1px solid ${colors.border}`, borderRadius: 6 };
  const rowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: 12 } as const;

  return (
    <section
      style={{
        margin: '0 24px 24px',
        padding: 16,
        borderRadius: 8,
        border: `1px solid ${isPending ? '#facc15' : colors.border}`,
        backgroundColor: isPending ? '#fefce8' : colors.bgSecondary,
      }}
    >
      <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Scale size={16} />
        {hasWeightLines ? 'Verify Weight & Recalculate Bill' : 'Final Bill'}
      </h3>

      {error ? (
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#dc2626', display: 'flex', gap: 6, alignItems: 'center' }}>
          <AlertCircle size={14} />
          {error}
        </p>
      ) : null}

      {isPending ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>
            Awaiting customer approval of the recalculated bill (₹{money(Number(order.final_amount || 0)).toFixed(2)}).
          </p>
          <div>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              style={{ border: 'none', background: colors.accent, color: '#fff', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
              <Send size={14} />
              {resending ? 'Sending…' : 'Resend SMS/Push'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {/* Per-weight-line verification */}
          {hasWeightLines ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {weightLines.map((line) => (
                <div key={line.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    {line.label} · ₹{line.unitPrice.toFixed(2)}/kg
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ fontSize: 12 }}>
                      Estimated Weight (Customer)
                      <div style={{ marginTop: 4, padding: 8, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 6, fontWeight: 600 }}>
                        {fmtWeight(line.origQty)} kg
                      </div>
                    </div>
                    <label style={{ fontSize: 12 }}>
                      Actual Weight (Hub)
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={actualWeights[line.id] ?? ''}
                        onChange={(e) => setActualWeights((prev) => ({ ...prev, [line.id]: parseNumberInput(e.target.value) }))}
                        placeholder="e.g. 3.5"
                        style={inputStyle}
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, textAlign: 'right' }}>
                    Line amount: <strong>₹{money(line.unitPrice * Number(actualWeights[line.id] || 0)).toFixed(2)}</strong>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <label style={{ fontSize: 12 }}>
            Inspected Item Count
            <input
              type="number"
              value={garmentCount}
              onChange={(e) => setGarmentCount(parseNumberInput(e.target.value))}
              placeholder="e.g. 12"
              style={inputStyle}
            />
          </label>

          {/* Additional items found at the hub */}
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Additional Items (found at hub)</div>
            {additionalItems.map((item, index) => (
              <div key={`additional-${index}`} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 120px auto', gap: 8, marginBottom: 8 }}>
                <input value={item.label} placeholder="Item name" onChange={(e) => setAdditionalItem(index, { label: e.target.value })} style={{ padding: 8, border: `1px solid ${colors.border}`, borderRadius: 6 }} />
                <input type="number" value={item.qty} onChange={(e) => setAdditionalItem(index, { qty: parseNumberInput(e.target.value) })} placeholder="Qty" style={{ padding: 8, border: `1px solid ${colors.border}`, borderRadius: 6 }} />
                <input type="number" value={item.unit_price} onChange={(e) => setAdditionalItem(index, { unit_price: parseNumberInput(e.target.value) })} placeholder="Unit price" style={{ padding: 8, border: `1px solid ${colors.border}`, borderRadius: 6 }} />
                <button type="button" onClick={() => removeAdditionalItem(index)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
                  <Trash2 size={16} color="#dc2626" />
                </button>
              </div>
            ))}
            <button type="button" onClick={addAdditionalItem} style={{ border: 'none', background: 'transparent', color: colors.accent, cursor: 'pointer', display: 'inline-flex', gap: 6 }}>
              <Plus size={14} />
              Add Item
            </button>
          </div>

          <label style={{ fontSize: 12 }}>
            Reason / Notes (max 500 chars)
            <textarea
              value={notes}
              maxLength={500}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Actual hub measurement"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </label>

          <label style={{ fontSize: 12 }}>
            Reweigh Photo (optional)
            <input type="file" accept="image/*" onChange={onPickPhoto} style={{ display: 'block', marginTop: 6 }} />
          </label>

          {/* Recalculated breakdown */}
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 12, background: colors.bgCard, display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
              <IndianRupee size={13} /> Recalculated Bill
            </div>
            {hasWeightLines ? (
              <div style={rowStyle}>
                <span>Weight</span>
                <span>{fmtWeight(estimatedWeight)} kg → <strong>{fmtWeight(actualWeight)} kg</strong></span>
              </div>
            ) : null}
            <div style={rowStyle}><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
            {discount > 0 ? (
              <div style={{ ...rowStyle, color: '#15803d' }}>
                <span>Coupon{order.coupon_code ? ` (${order.coupon_code})` : ''}</span>
                <span>−₹{discount.toFixed(2)}</span>
              </div>
            ) : null}
            {deliveryFee > 0 ? (<div style={rowStyle}><span>Delivery</span><span>₹{deliveryFee.toFixed(2)}</span></div>) : null}
            <div style={{ ...rowStyle, fontWeight: 700, borderTop: `1px solid ${colors.border}`, paddingTop: 6, marginTop: 2 }}>
              <span>Grand Total</span><span>₹{grandTotal.toFixed(2)}</span>
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>Inclusive of all applicable taxes and charges.</div>
            <div style={{ ...rowStyle, color: totalDelta > 0 ? '#dc2626' : '#15803d' }}>
              <span>Estimate was ₹{estimatedTotal.toFixed(2)}</span>
              <span>{totalDelta >= 0 ? '+' : ''}₹{totalDelta.toFixed(2)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              style={{ border: 'none', background: colors.accent, color: '#fff', borderRadius: 6, padding: '10px 16px', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, fontWeight: 600 }}
            >
              {loading ? 'Posting…' : 'Recalculate & Post Final Bill'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
